import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { isMotionPaused } from '@/lib/motion'
import { formatNumber } from '@/components/generals/count-up'

interface TweenNumberProps {
    value: number
    decimals?: number
    suffix?: string
    duration?: number
    className?: string
}

/**
 * A number that glides from whatever it showed last to its new value.
 * Jumps straight to the value when motion is reduced or paused.
 */
export const TweenNumber: FC<TweenNumberProps> = ({ value, decimals = 0, suffix = '', duration = 800, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const shown = useRef(value)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        const from = shown.current
        const write = (current: number): void => {
            shown.current = current
            element.textContent = `${formatNumber(current, decimals)}${suffix}`
        }

        if (from === value || isMotionPaused() || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            write(value)

            return
        }

        let raf = 0
        const start = performance.now()

        const frame = (now: number): void => {
            const progress = Math.min(1, (now - start) / duration)
            const eased = 1 - Math.pow(1 - progress, 3)

            write(from + (value - from) * eased)

            if (progress < 1) raf = requestAnimationFrame(frame)
        }

        raf = requestAnimationFrame(frame)

        return () => {
            cancelAnimationFrame(raf)
            write(value)
        }
    }, [value, decimals, suffix, duration])

    return (
        <span ref={ref} className={className ?? 'tabular-nums'}>
            {`${formatNumber(value, decimals)}${suffix}`}
        </span>
    )
}
